import { useCallback, useState } from 'react';

import { useQueryState } from 'hooks/useQueryState';

import { ApprovalsTab } from './ApprovalsTabs';
import { readChoice, rememberChoice } from './rememberedChoice';

export const TAB_STORAGE_KEY = 'approvals.selectedTab';

/**
 * Which approvals tab is open, and how that survives leaving the page.
 *
 * Three sources, in descending precedence:
 *
 *   `?tab=` in the URL — an explicit request, so it wins. It is also what makes
 *     a tab linkable and survives a refresh; `opening_approvals` already takes
 *     a tab in its query string, and the monolith deep-links into it.
 *   localStorage — the tab this admin last chose, which is the "remembered"
 *     part of the requirement: unlike the query string it survives navigating
 *     away to settings and coming back.
 *   `tabs[0]` — the default, Approver rules.
 *
 * A remembered or linked id that names no current tab falls through to the
 * default rather than selecting nothing: tabs come and go as the sibling
 * tickets land, and a stale id in someone's browser must not produce a page
 * with no tab selected and no panel rendered.
 */
export function useApprovalsTab(
  tabs: ApprovalsTab[],
): [string, (id: string) => void] {
  const fallback = tabs[0].id;
  // Read once. Re-reading each render would let a write further down flip the
  // fallback mid-session, which is invisible while a `?tab=` is set and
  // confusing on the render where it is cleared.
  const [remembered] = useState(() => readChoice(TAB_STORAGE_KEY));
  const known = (id: string | null) =>
    tabs.some(tab => tab.id === id) ? (id as string) : fallback;

  const [queryTab, setQueryTab] = useQueryState('tab', known(remembered));

  const select = useCallback(
    (id: string) => {
      rememberChoice(TAB_STORAGE_KEY, id);
      setQueryTab(id);
    },
    [setQueryTab],
  );

  return [known(queryTab), select];
}
